import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { looksLikeMarkdown, markdownToHtml, htmlIsSemantic } from '../markdownPaste';

/* MarkdownPaste — convert pasted markdown SOURCE into real blocks.
 *
 * The detection and conversion live in ../markdownPaste.js (pure, tested). This
 * file is only the clipboard decision: WHEN to intervene. That decision is the
 * whole design, because a paste handler that fires when it shouldn't rewrites
 * text the user pasted verbatim — a silent, invisible corruption. So every
 * branch below defaults to standing down.
 *
 * Four ways it stands down, each for a concrete reason:
 *   1. Shift-paste (Cmd/Ctrl+Shift+V) — the universal "paste as plain text"
 *      gesture. If we convert there, the user has no escape hatch at all.
 *   2. Inside a code block — markdown pasted into a code fence is source code
 *      being quoted, and converting it is exactly backwards.
 *   3. Clipboard HTML with real structure — copied from a rendered page, the
 *      HTML is higher fidelity than re-parsing its plain-text fallback.
 *   4. Text that isn't confidently markdown — see looksLikeMarkdown.
 */
const MarkdownPaste = Extension.create({
	name: 'markdownPaste',

	addProseMirrorPlugins() {
		const { editor } = this;

		return [
			new Plugin({
				key: new PluginKey('markdownPaste'),
				props: {
					handlePaste: (view, event) => {
						const clipboard = event.clipboardData;
						if (!clipboard) return false;

						/* `view.input.shiftKey` is how prosemirror-view itself records
						   plain-text paste intent (it becomes `preferPlain` in doPaste).
						   Semi-private, hence the optional chaining — if a future version
						   drops it we lose the escape hatch, not correctness. */
						if (view.input?.shiftKey) return false;

						// spec.code covers codeBlock and any other code-ish node.
						if (view.state.selection.$from.parent.type.spec.code) return false;

						const text = clipboard.getData('text/plain');
						if (!looksLikeMarkdown(text)) return false;

						if (htmlIsSemantic(clipboard.getData('text/html'))) return false;

						const html = markdownToHtml(text);
						if (!html) return false;

						/* Let TipTap parse through the live schema, so a mount without a
						   given capability degrades instead of failing: a table pasted
						   into an editor with no table node arrives as its text, not as
						   an error. */
						editor.commands.insertContent(html);
						return true;
					},
				},
			}),
		];
	},
});

export default MarkdownPaste;
